/**
 * A reusable presentational component that handles the RTR breakdown information
 * that must be displayed on the success page after a successful transaction if
 * remittance transfer rul was triggered. It displays a table with rows for
 * "you sent, fees, you paid, so-and-so received"
 */

import PropTypes from 'prop-types'

import React from 'react'
import classNames from 'classnames'
import { StyleSheet, css } from 'lib/paypalphrodite'
import content from 'pp-react-l10n'
import {
  getDisclaimerMessageForRTRConfirmation,
  getENDisclaimerMessageForRTRConfirmation,
} from '../utils/currencyConversionFeeDisclaimer-utils'
import { shouldShowCurrencyConversionLink } from '../utils/currencyConversionUtils'
import enRTRStrings from '../../../../config/enRTRStrings'
import { isDw2Repaint } from '../../experiments/dw2Repaint'
import { BodyText, CaptionText, v2grey600 } from '@paypalcorp/pp-react'
import glamorous from 'glamorous'
import { hasGuestFlowRevamp } from '../../experiments/guest'
let i18n = content('transfer/inc/rtr')

const primaryTextStyle = {
  fontSize: '16px',
  lineHeight: '24px',
  color: '#2C2E2F',
}
const subTextStyle = {
  fontSize: '14px',
  lineHeight: '20px',
  color: '#687173',
}

const styles = StyleSheet.create({
  fundsAvailability: {
    margin: '2em 0 0 0',
    fontWeight: 400,
    ...primaryTextStyle,
  },
  fundsAvailabilityENSubText: {
    margin: 0,
    fontWeight: 400,
    ...subTextStyle,
  },
  table: {
    width: '100%',
    marginTop: '25px',
  },
  rowBorder: {
    borderBottom: '1px dashed #9DA3A6',
  },
  paidKey: {
    paddingBottom: '0px',
  },
  tdPad: {
    padding: '16px 2px',
  },
  txtLeft: {
    textAlign: 'left',
  },
  txtRight: {
    textAlign: 'right',
    verticalAlign: 'top',
  },
  thirdParty: {
    marginTop: '25px',
  },
  conversion: {
    ...primaryTextStyle,
    margin: '20px 0 0',
    textAlign: 'left',
  },
  conversionENSubText: {
    ...subTextStyle,
    textAlign: 'left',
    margin: 0,
  },
  keyText: {
    ...primaryTextStyle,
  },
  keyENSubText: {
    paddingTop: '4px',
    ...subTextStyle,
  },
  disclaimerMessage: {
    margin: '8px 0',
    textAlign: 'left',
    ...primaryTextStyle,
  },
  disclaimerMessageENSubText: {
    marginBottom: '8px',
    textAlign: 'left',
    ...subTextStyle,
  },
})

const SuccessFeeDisplayDiv = glamorous.div({
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: '1rem',
  textAlign: 'left',
})

const CurrencyDisclaimerDiv = glamorous.div({
  textAlign: 'left',
  marginTop: '1rem',
})

class RTRSuccessBreakdown extends React.Component {
  static propTypes = {
    isGoods: PropTypes.bool.isRequired,
    selectedFundingOption: PropTypes.shape({
      displayAmounts: PropTypes.shape({
        fee: PropTypes.string, // fee amount, that when added to amount, equals total
        paid: PropTypes.string, // the amount the sender is paying (including fees)
        received: PropTypes.string, // the amount the recipient will actually receive
        sent: PropTypes.string, // the amount the sender sent (in their own currency)
      }),
      currency_exchange: PropTypes.shape({
        exchange_funds_in: PropTypes.shape({
          currency_code: PropTypes.string,
        }),
        exchange_funds_out: PropTypes.shape({
          currency_code: PropTypes.string,
        }),
        exchange_rate: PropTypes.string,
      }),
    }),
    complianceInfo: PropTypes.shape({
      fundsAvailabilityDateIsValid: PropTypes.bool,
      fundsAvailabilityDate: PropTypes.string,
      status: PropTypes.string,
    }),
    recipient: PropTypes.string.isRequired,
    displayName: PropTypes.string,
    recipientCountry: PropTypes.string, // TODO: how can we get this for non-Pay Request flows?
    env: PropTypes.string,
    showENSubTextRTR: PropTypes.bool,
  }

  static defaultProps = {
    env: '',
    recipientCountry: '',
  }

  renderYouSentRow = () => {
    const {
      selectedFundingOption: {
        displayAmounts: { sent },
      },
      showENSubTextRTR,
    } = this.props
    const tableRowClasses = classNames('tableRow', css(styles.rowBorder))
    return isDw2Repaint() || hasGuestFlowRevamp() ? (
      <SuccessFeeDisplayDiv>
        <div>
          <BodyText>{i18n('success.youSent')}</BodyText>
          {showENSubTextRTR && (
            <CaptionText style={{ color: v2grey600 }}>
              {enRTRStrings.youSent}
            </CaptionText>
          )}
        </div>
        <BodyText>{sent}</BodyText>
      </SuccessFeeDisplayDiv>
    ) : (
      <tr className={tableRowClasses}>
        <td id="sentKey" className={css(styles.txtLeft, styles.tdPad)}>
          <tr className={css(styles.keyText)}>{i18n('success.youSent')}</tr>
          {showENSubTextRTR && (
            <tr className={css(styles.keyENSubText)}>{enRTRStrings.youSent}</tr>
          )}
        </td>
        <td id="sentValue" className={css(styles.txtRight, styles.tdPad)}>
          {sent}
        </td>
      </tr>
    )
  }

  renderFeeRow = () => {
    const {
      selectedFundingOption: {
        displayAmounts: { fee },
      },
      showENSubTextRTR,
    } = this.props
    const tableRowClasses = classNames('tableRow', css(styles.rowBorder))
    return isDw2Repaint() || hasGuestFlowRevamp() ? (
      <SuccessFeeDisplayDiv>
        <div>
          <BodyText>{i18n('success.fee')}</BodyText>
          {showENSubTextRTR && (
            <CaptionText style={{ color: v2grey600 }}>
              {enRTRStrings.fee}
            </CaptionText>
          )}
        </div>
        <BodyText>{fee}</BodyText>
      </SuccessFeeDisplayDiv>
    ) : (
      <tr className={tableRowClasses}>
        <td id="feeKey" className={css(styles.txtLeft, styles.tdPad)}>
          <tr className={css(styles.keyText)}>{i18n('success.fee')}</tr>
          {showENSubTextRTR && (
            <tr className={css(styles.keyENSubText)}>{enRTRStrings.fee}</tr>
          )}
        </td>
        <td id="feeValue" className={css(styles.txtRight, styles.tdPad)}>
          {fee}
        </td>
      </tr>
    )
  }

  renderYouPaidRow = () => {
    const {
      selectedFundingOption: {
        displayAmounts: { paid },
      },
      showENSubTextRTR,
    } = this.props
    const tableRowClasses = classNames('tableRow')
    return isDw2Repaint() || hasGuestFlowRevamp() ? (
      <SuccessFeeDisplayDiv>
        <div>
          <BodyText strong>{i18n('success.youPaid')}</BodyText>
          {showENSubTextRTR && (
            <CaptionText style={{ color: v2grey600 }}>
              {enRTRStrings.youPaid}
            </CaptionText>
          )}
        </div>
        <BodyText strong>{paid}</BodyText>
      </SuccessFeeDisplayDiv>
    ) : (
      <tr className={tableRowClasses}>
        <td
          id="paidKey"
          className={css(styles.txtLeft, styles.tdPad, styles.paidKey)}
        >
          <tr className={css(styles.keyText)}>{i18n('success.youPaid')}</tr>
          {showENSubTextRTR && (
            <tr className={css(styles.keyENSubText)}>{enRTRStrings.youPaid}</tr>
          )}
        </td>
        <td id="paidValue" className={css(styles.txtRight, styles.tdPad)}>
          {paid}
        </td>
      </tr>
    )
  }

  renderRecipientWillGetRow = () => {
    const {
      recipient,
      displayName,
      selectedFundingOption: {
        displayAmounts: { received },
      },
      showENSubTextRTR,
    } = this.props
    const name = displayName || recipient
    const tableRowClasses = classNames('tableRow', css(styles.rowBorder))
    return isDw2Repaint() || hasGuestFlowRevamp() ? (
      <SuccessFeeDisplayDiv>
        <div>
          <BodyText>
            {i18n('success.recipientName', { recipient: name })}
          </BodyText>
          {showENSubTextRTR && (
            <CaptionText style={{ color: v2grey600 }}>
              {`${name} ${enRTRStrings.recipientName}`}
            </CaptionText>
          )}
        </div>
        <BodyText>{received}</BodyText>
      </SuccessFeeDisplayDiv>
    ) : (
      <tr className={tableRowClasses}>
        <td id="receiverKey" className={css(styles.txtLeft, styles.tdPad)}>
          <tr className={css(styles.keyText)}>
            {i18n('success.recipientName', { recipient: name })}
          </tr>
          {showENSubTextRTR && (
            <tr
              className={css(styles.keyENSubText)}
            >{`${name} ${enRTRStrings.recipientName}`}</tr>
          )}
        </td>
        <td id="receiverValue" className={css(styles.txtRight, styles.tdPad)}>
          {received}
        </td>
      </tr>
    )
  }

  getConversionDetails = () => {
    const {
      selectedFundingOption: {
        currency_exchange: {
          exchange_funds_in = {},
          exchange_funds_out = {},
          exchange_rate = '',
        },
      },
    } = this.props

    return {
      fundsInCode: exchange_funds_in.currency_code,
      fundsOutCode: exchange_funds_out.currency_code,
      exchangeRate: exchange_rate,
    }
  }

  render() {
    const {
      isGoods,
      recipientCountry,
      complianceInfo: {
        fundsAvailabilityDateIsValid,
        fundsAvailabilityDate,
        status,
      },
      selectedFundingOption,
      showENSubTextRTR,
    } = this.props
    const tableClasses = classNames('fundingOptionTable', css(styles.table))
    const convertUsingCard = shouldShowCurrencyConversionLink(
      selectedFundingOption
    )
    const currencyExchange = selectedFundingOption.currency_exchange
    const convertWithPayPal = !!currencyExchange
    const userCountry = 'US' // RTR rule only applies to US sender
    const feeDisclaimerMessage = getDisclaimerMessageForRTRConfirmation({
      convertWithPayPal,
      userCountry,
      convertUsingCard,
      isPurchase: isGoods,
    })
    const feeDisclaimerMessageENSubText = getENDisclaimerMessageForRTRConfirmation(
      {
        convertWithPayPal,
        userCountry,
        convertUsingCard,
        isPurchase: isGoods,
      }
    )
    const isOFAC = status === 'PENDING'

    return isDw2Repaint() || hasGuestFlowRevamp() ? (
      <div data-nemo="funding-option-table">
        {!isOFAC && fundsAvailabilityDateIsValid && (
          <div style={{ marginBottom: '2rem' }}>
            <BodyText>
              {i18n('rtr.fundsAvailabilityDate')}: {fundsAvailabilityDate}
            </BodyText>
            {showENSubTextRTR && (
              <CaptionText style={{ color: v2grey600 }}>
                {enRTRStrings.fundsAvailabilityDate}: {fundsAvailabilityDate}
              </CaptionText>
            )}
          </div>
        )}
        <div>
          {!isGoods && this.renderYouSentRow()}
          {!isGoods && this.renderFeeRow()}
          {this.renderYouPaidRow()}
          {!isGoods && this.renderRecipientWillGetRow()}
        </div>

        {!!currencyExchange && (
          <CurrencyDisclaimerDiv>
            <BodyText>
              {i18n('success.reactConversionRate', this.getConversionDetails())}
            </BodyText>
            {showENSubTextRTR && (
              <CaptionText style={{ color: v2grey600 }}>
                {enRTRStrings.reactConversionRate}
              </CaptionText>
            )}
          </CurrencyDisclaimerDiv>
        )}
        <CurrencyDisclaimerDiv>
          <BodyText>{feeDisclaimerMessage}</BodyText>
          {showENSubTextRTR && (
            <CaptionText style={{ color: v2grey600 }}>
              {feeDisclaimerMessageENSubText}
            </CaptionText>
          )}
        </CurrencyDisclaimerDiv>

        {/* Conditionally handle one-off Indian compliance rule */}
        {recipientCountry === 'IN' && (
          <BodyText>{i18n('rtr.thirdPartyFees')}</BodyText>
        )}
      </div>
    ) : (
      <div
        className="fundingOptionTable_wrapper"
        data-nemo="funding-option-table"
      >
        {!isOFAC && fundsAvailabilityDateIsValid && (
          <>
            <p className={`${css(styles.fundsAvailability)} vx_text-5`}>
              {i18n('rtr.fundsAvailabilityDate')}: {fundsAvailabilityDate}
            </p>
            {showENSubTextRTR && (
              <p
                className={`${css(
                  styles.fundsAvailabilityENSubText
                )} vx_text-5`}
              >
                {enRTRStrings.fundsAvailabilityDate}: {fundsAvailabilityDate}
              </p>
            )}
          </>
        )}
        <table className={tableClasses}>
          <tbody>
            {!isGoods && this.renderYouSentRow()}
            {!isGoods && this.renderFeeRow()}
            {this.renderYouPaidRow()}
            {!isGoods && this.renderRecipientWillGetRow()}
          </tbody>
        </table>

        {!!currencyExchange && (
          <>
            <p
              className={classNames(
                css(styles.conversion),
                'test_conversion-rate'
              )}
            >
              {i18n('success.reactConversionRate', this.getConversionDetails())}
            </p>
            {showENSubTextRTR && (
              <p className={css(styles.conversionENSubText)}>
                {enRTRStrings.reactConversionRate}
              </p>
            )}
          </>
        )}
        <div className={css(styles.disclaimerMessage)}>
          {feeDisclaimerMessage}
        </div>
        {showENSubTextRTR && (
          <div className={css(styles.disclaimerMessageENSubText)}>
            {feeDisclaimerMessageENSubText}
          </div>
        )}

        {/* Conditionally handle one-off Indian compliance rule */}
        {recipientCountry === 'IN' && (
          <p className={classNames(css(styles.thirdParty), 'thirdPartyInfo')}>
            {i18n('rtr.thirdPartyFees')}
          </p>
        )}
      </div>
    )
  }
}

export default RTRSuccessBreakdown
